import React from 'react';
import '../../styles/modal.css';

/** One labelled control in an edit modal, with optional explanation underneath. */
export const Field: React.FC<{ label: string; hint?: string; wide?: boolean; children: React.ReactNode }> =
  ({ label, hint, wide, children }) => (
    <div style={{ marginBottom: '0.85rem', gridColumn: wide ? '1 / -1' : undefined }}>
      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '3px', color: '#334155' }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ marginTop: '3px', fontSize: '0.72rem', color: '#64748b', lineHeight: 1.35 }}>{hint}</div>}
    </div>
  );

/** A heading inside the modal body, so a long form reads as a few short ones. */
export const FieldGroup: React.FC<{ title: string }> = ({ title }) => (
  <div style={{
    gridColumn: '1 / -1', marginTop: '0.4rem', marginBottom: '0.3rem', fontSize: '0.72rem',
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8'
  }}>
    {title}
  </div>
);

/**
 * Edit one row in a dialog rather than inside the table. A table row can't hold a dozen labelled
 * controls without overflowing sideways, which is how the inline version ended up unreadable.
 */
const EditModal: React.FC<{
  title: string;
  onClose: () => void;
  onDelete?: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, onDelete, children }) => (
  // Nothing dismisses this but the two buttons. Closing on a stray click beside the panel, or on a
  // key pressed while filling it in, throws away a form someone is halfway through.
  <div className="modal-overlay" role="presentation">
    <div className="modal-content" role="dialog" aria-modal="true" aria-label={title}
      style={{ width: 'min(94vw, 640px)', padding: 0 }}
    >
      <div className="modal-header" style={{ padding: '0.9rem 1.25rem', position: 'relative' }}>
        <h2 className="modal-title" style={{ margin: 0, fontSize: '1.05rem' }}>{title}</h2>
        <button
          onClick={onClose}
          aria-label="Close"
          title="Close"
          style={{
            position: 'absolute', top: '50%', right: '1rem', transform: 'translateY(-50%)',
            border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.4rem',
            lineHeight: 1, color: '#64748b', padding: '0 4px',
          }}
        >
          ×
        </button>
      </div>
      <div style={{
        padding: '1rem 1.25rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
          gap: '0 1.1rem',
          maxHeight: '65vh',
          overflowY: 'auto'
        }}>
          {children}
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: '0.5rem',
          padding: '0.8rem 1.25rem', borderTop: '1px solid #e5e7eb'
        }}>
          {onDelete
            ? (
              <button onClick={onDelete} style={{
                padding: '6px 14px', background: '#fff', color: '#dc2626',
                border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer'
              }}>
                Delete
              </button>
            )
            : <span />}
          <button onClick={onClose} style={{
            padding: '6px 18px', background: '#10b981', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600
          }}>
            Done
          </button>
        </div>
    </div>
  </div>
);

export default EditModal;
